import orderApi from "@api/orderApi";
import returnProductManagementApi, {
  CreateReturnProductAppBySourceOrderPayload,
  ProductPayload,
  ReturnInvoiceInfo,
  ReturnProductAppItem,
  ReturnProductAppList,
  ReturnProductAppListParams,
  ReturnProductItem,
  ReturnSourceContext,
  ReturnSourceType,
  UpdateAppDetailPayload,
} from "@api/sharing/ReturnProductManagementApi";
import onlineOrderApi from "@api/throughShipment/onlineOrderApi";
import throughShipmentApi, { RegionType, RetailList } from "@api/throughShipment/throughShipmentApi";
import type { RootState } from "@redux/rootReducer";
import { translateErrors } from "@utils/translateError";
import { ListResponseType } from "src/types";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Modal, message } from "antd";

export interface SourceOrderPrefill {
  originalPlatformNumber: string;
  platform: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  receiverZipcode: number | null;
}

export interface SourceOrderProductItem {
  productId: string;
  sku: string | null;
  productName: string;
  batchNumber: string | null;
  effectiveDate: string | null;
  originalQty: number;
}

interface IState {
  isFetching: boolean;
  returnProductAppListParams: ReturnProductAppListParams;
  returnProductAppList: ReturnProductAppList;
  returnProductAppDetail?: ReturnProductAppItem;
  returnProductList: ListResponseType<ReturnProductItem>;
  retailList?: RetailList[];
  importErrorResults?: string[];
}

const initialState: IState = {
  isFetching: false,
  returnProductAppListParams: {
    limit: 20,
    offset: 0,
  },
  returnProductAppList: {
    count: 0,
    next: "",
    previous: "",
    results: [],
    staffs: [],
  },
  returnProductAppDetail: undefined,
  returnProductList: {
    count: 0,
    next: "",
    previous: "",
    results: [],
  },
  retailList: undefined,
  importErrorResults: undefined,
};

// 退貨單：
export const fetchReturnProductAppList = createAsyncThunk(
  "returnProductManagement/fetchReturnWarehouseList",
  async (params: ReturnProductAppListParams, thunkApi) => {
    try {
      const response = await returnProductManagementApi.fetchReturnProductAppList(params);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const importReturnProductApp = createAsyncThunk(
  "returnProductManagement/importReturnProductApp",
  async (payload: File, thunkApi) => {
    try {
      const response = await returnProductManagementApi.importReturnProductApp(payload);
      message.success("匯入成功");
      thunkApi.dispatch(fetchReturnProductAppList({ limit: 20, offset: 0 }));
      return response;
    } catch (error: any) {
      const { result } = error;
      const translatedErrors = translateErrors(result);
      thunkApi.dispatch(updateImportErrorResults(translatedErrors));
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const updateReturnProductApp = createAsyncThunk(
  "returnProductManagement/updateReturnProductApp",
  async (data: { returnId: number; payload: UpdateAppDetailPayload }, thunkApi) => {
    try {
      const { returnId, payload } = data;
      const response = await returnProductManagementApi.updateReturnProductApp(returnId, payload);
      thunkApi.dispatch(fetchReturnProductAppDetail(returnId));
      message.success("更新成功");
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const deleteReturnProductApp = createAsyncThunk(
  "returnProductManagement/deleteReturnProductApp",
  async (returnId: number, thunkApi) => {
    try {
      const {
        ReturnProductManagementSlice: { returnProductAppListParams },
      } = thunkApi.getState() as RootState;
      const response = await returnProductManagementApi.deleteReturnProductApp(returnId);
      thunkApi.dispatch(fetchReturnProductAppList(returnProductAppListParams));
      message.success("作廢成功");
      return response;
    } catch (error) {
      message.error("作廢失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const fetchReturnPlatformList = createAsyncThunk(
  "returnProductManagement/fetchReturnPlatformList",
  async (payload: { contractTypes?: number[] }, thunkApi) => {
    try {
      const response = await throughShipmentApi.fetchRetailList(payload);
      // BE不會建立"Urmart線上/實體/回倉"在通路中,所以手動加入
      response.results.push(
        {
          id: -1,
          name: "Urmart 線上",
          address: "",
          phone: "",
          region: {
            id: -1,
            name: "",
            regionType: RegionType.DOMESTIC_CHANNEL,
          },
        },
        {
          id: -2,
          name: "Urmart 回倉",
          address: "",
          phone: "",
          region: {
            id: -1,
            name: "",
            regionType: RegionType.DOMESTIC_CHANNEL,
          },
        },
        {
          id: -3,
          name: "Urmart 實體",
          address: "",
          phone: "",
          region: {
            id: -1,
            name: "",
            regionType: RegionType.DOMESTIC_CHANNEL,
          },
        },
      );
      return response.results;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const backfillInvoice = createAsyncThunk(
  "returnProductManagement/backfillInvoice",
  async (data: { returnId: number; payload: ReturnInvoiceInfo }, thunkApi) => {
    try {
      const {
        ReturnProductManagementSlice: { returnProductAppListParams },
      } = thunkApi.getState() as RootState;
      const { returnId, payload } = data;
      const response = await returnProductManagementApi.backfillInvoice(returnId, payload);
      thunkApi.dispatch(fetchReturnProductAppList(returnProductAppListParams));
      message.success("開立成功");
      return response;
    } catch (error) {
      message.error("開立失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
// 退貨單詳情：
export const fetchReturnProductAppDetail = createAsyncThunk(
  "returnProductManagement/fetchReturnProductAppDetail",
  async (returnId: number, thunkApi) => {
    try {
      const response = await returnProductManagementApi.fetchReturnProductAppDetail(returnId);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const applyReturnProductApp = createAsyncThunk(
  "returnProductManagement/applyReturnProductApp",
  async (returnId: number, thunkApi) => {
    try {
      const response = await returnProductManagementApi.applyReturnProductApp(returnId);
      thunkApi.dispatch(fetchReturnProductAppDetail(returnId));
      message.success("提交成功");
      return response;
    } catch (error) {
      message.error("提交失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
// 退貨商品：
export const fetchReturnProductList = createAsyncThunk(
  "returnProductManagement/fetchReturnProductList",
  async (returnId: number, thunkApi) => {
    try {
      const response = await returnProductManagementApi.fetchReturnProductList(returnId);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const createReturnProduct = createAsyncThunk(
  "returnProductManagement/createReturnProduct",
  async (data: { returnId: number; payload: ProductPayload }, thunkApi) => {
    try {
      const { returnId, payload } = data;
      const response = await returnProductManagementApi.createReturnProduct(returnId, payload);
      message.success("新增成功");
      thunkApi.dispatch(fetchReturnProductList(returnId));
      return response;
    } catch (error) {
      message.error("新增失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const updateReturnProduct = createAsyncThunk(
  "returnProductManagement/updateReturnProduct",
  async (data: { returnId: number; productId: number; payload: ProductPayload }, thunkApi) => {
    try {
      const { returnId, productId, payload } = data;
      const response = await returnProductManagementApi.updateReturnProduct(returnId, productId, payload);
      thunkApi.dispatch(fetchReturnProductList(returnId));
      message.success("更新成功");
      return response;
    } catch (error) {
      message.error("更新失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const deleteReturnProduct = createAsyncThunk(
  "returnProductManagement/deleteReturnProduct",
  async (data: { returnId: number; productId: number }, thunkApi) => {
    try {
      const { returnId, productId } = data;
      const response = await returnProductManagementApi.deleteReturnProduct(returnId, productId);
      thunkApi.dispatch(fetchReturnProductList(returnId));
      message.success("刪除成功");
      return response;
    } catch (error) {
      message.error("刪除失敗");
      return thunkApi.rejectWithValue(error);
    }
  },
);
export const fetchProductInfo = createAsyncThunk(
  "returnProductManagement/fetchProductInfo",
  async (data: { sku: string; warehouseCode: string }, thunkApi) => {
    try {
      const { sku, warehouseCode } = data;
      const response = await returnProductManagementApi.fetchProductInfo(sku, warehouseCode);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

// 建立退貨單：Step1 進畫面時，依來源模組向各自既有 API 撈取通路/退件人資料並統一成同一種前端形狀
export const fetchSourceOrderPrefill = createAsyncThunk(
  "returnProductManagement/fetchSourceOrderPrefill",
  async (data: ReturnSourceContext, thunkApi) => {
    try {
      const { sourceType, orderId, subOrderId } = data;
      if (sourceType === ReturnSourceType.EXTERNAL_ORDER) {
        const info = await throughShipmentApi.fetchExternalOrderInfo(orderId);
        return {
          originalPlatformNumber: info.orderNumber,
          platform: info.retailerName,
          receiverName: info.retailerContactName,
          receiverPhone: info.retailerContactPhone,
          receiverAddress: info.retailerWarehouseAddress,
          receiverZipcode: null,
        };
      }
      if (sourceType === ReturnSourceType.ONLINE_ORDER) {
        const info = await onlineOrderApi.fetchOrderDetail(orderId);
        return {
          originalPlatformNumber: info.orderNumber,
          platform: info.platform,
          receiverName: info.receiverName,
          receiverPhone: info.receiverPhone,
          receiverAddress: info.receiverAddress,
          receiverZipcode: info.receiverZipcode ? Number(info.receiverZipcode) : null,
        };
      }
      const info = await orderApi.fetchOrderInfo(orderId);
      const shipment = info.orderShipments.find((item) => item.id === subOrderId);
      return {
        originalPlatformNumber: shipment?.shipperNumber || "",
        platform: info.orderInfo.source.platformText,
        receiverName: shipment?.receiverName || "",
        receiverPhone: shipment?.receiverPhone || "",
        receiverAddress: shipment?.address || "",
        receiverZipcode: null,
      };
    } catch (error) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue(error.message);
      }
      return thunkApi.rejectWithValue(error);
    }
  },
);

// 建立退貨單：Step2 進畫面時，依來源模組向各自既有 API 撈取商品明細並統一成同一種前端形狀
export const fetchSourceOrderProductList = createAsyncThunk(
  "returnProductManagement/fetchSourceOrderProductList",
  async (data: ReturnSourceContext, thunkApi) => {
    try {
      const { sourceType, orderId, subOrderId } = data;
      if (sourceType === ReturnSourceType.EXTERNAL_ORDER) {
        const info = await throughShipmentApi.fetchExternalOrderInfo(orderId);
        return info.externalOrderLine.map((line) => ({
          productId: String(line.id),
          sku: line.sku,
          productName: line.name,
          batchNumber: line.batchNumber,
          effectiveDate: line.effectiveDate,
          originalQty: line.shippedQty,
        }));
      }
      if (sourceType === ReturnSourceType.ONLINE_ORDER) {
        const response = await onlineOrderApi.fetchProductList({ orderId, filter: { limit: 200, offset: 0 } });
        return response.results.map((item) => ({
          productId: String(item.id),
          sku: item.sku,
          productName: item.productName,
          batchNumber: item.batch,
          effectiveDate: item.effectiveDate,
          originalQty: item.actualQty,
        }));
      }
      const info = await orderApi.fetchOrderInfo(orderId);
      const shipment = info.orderShipments.find((item) => item.id === subOrderId);
      return (shipment?.orderSalesPlans || []).flatMap((plan) =>
        plan.salesPlanOptions.map((option) => ({
          productId: String(option.productId),
          // 訂單內容子訂單來源無真實 SKU，避免拿 product_id 冒充 SKU 顯示
          sku: null,
          productName: option.optionName,
          // 訂單內容子訂單來源目前 API 無 effectiveDate／batch 欄位，待 BE 補齊
          batchNumber: null,
          effectiveDate: null,
          originalQty: option.optionQty,
        })),
      );
    } catch (error) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue(error.message);
      }
      return thunkApi.rejectWithValue(error);
    }
  },
);

// 建立退貨單：Step3 確認送出，整個建立流程中唯一一次寫入 API 呼叫
export const createReturnProductAppBySourceOrder = createAsyncThunk(
  "returnProductManagement/createReturnProductAppBySourceOrder",
  async (payload: CreateReturnProductAppBySourceOrderPayload, thunkApi) => {
    try {
      const response = await returnProductManagementApi.createReturnProductAppBySourceOrder(payload);
      Modal.success({
        title: `退貨單單號：${response.returnNumber}`,
        content: "已成功建立，您可於退貨管理檢視",
        okText: "我知道了",
      });
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const ReturnProductManagementSlice = createSlice({
  name: "returnProduct",
  initialState,
  reducers: {
    resetSlice: () => initialState,
    updateAppListParams: (state, action) => {
      state.returnProductAppListParams = action.payload;
    },
    resetAppListParams: (state) => {
      state.returnProductAppListParams = initialState.returnProductAppListParams;
    },
    updateImportErrorResults: (state, action) => {
      state.importErrorResults = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReturnProductAppList.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchReturnProductAppList.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(fetchReturnProductAppList.fulfilled, (state, action) => {
      state.isFetching = false;
      state.returnProductAppList = action.payload;
    });
    builder.addCase(fetchReturnPlatformList.fulfilled, (state, action) => {
      state.retailList = action.payload;
    });
    builder.addCase(fetchReturnProductAppDetail.pending, (state, action) => {
      state.isFetching = true;
    });
    builder.addCase(fetchReturnProductAppDetail.rejected, (state, action) => {
      state.isFetching = false;
    });
    builder.addCase(fetchReturnProductAppDetail.fulfilled, (state, action) => {
      state.isFetching = false;
      state.returnProductAppDetail = action.payload;
    });
    builder.addCase(fetchReturnProductList.pending, (state, action) => {
      state.isFetching = true;
    });
    builder.addCase(fetchReturnProductList.rejected, (state, action) => {
      state.isFetching = false;
    });
    builder.addCase(fetchReturnProductList.fulfilled, (state, action) => {
      state.isFetching = false;
      state.returnProductList = action.payload;
    });
  },
});

export const { resetSlice, updateAppListParams, resetAppListParams, updateImportErrorResults } =
  ReturnProductManagementSlice.actions;
export default ReturnProductManagementSlice.reducer;

export const returnProductState = (state: RootState) => state.ReturnProductManagementSlice;
